import { Link } from "react-router-dom";
import { ArrowRight, Globe, Shield, Wrench, Phone } from "lucide-react";
import { CATEGORIES } from "@/data/categories";

const HERO_IMG = "/assets/wix-equipment.png";
const ABOUT_IMG = "/assets/wix-factory.jpg";

export default function Home() {
  return (
    <div className="bg-slate-50">
      {/* HERO */}
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Mining excavator" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-amber-500" />
              <span className="font-mono text-xs tracking-[0.3em] uppercase text-amber-500">
                Henan Greatwall Machinery Co., Ltd
              </span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.95]">
              Equipamiento completo<br />
              <span className="text-amber-500">para la minería</span><br />
              y producción de áridos
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              CONSA es Great Wall Machinery en Latinoamérica, España y Portugal.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/catalogo"
                data-testid="hero-catalog-btn"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-7 py-4 text-sm btn-brutal inline-flex items-center gap-2"
              >
                Explorar catálogo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contacto"
                data-testid="hero-cotizar-btn"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-950 font-display font-black uppercase tracking-wider px-7 py-4 text-sm transition-colors"
              >
                Cotizar maquinaria
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex flex-col gap-4">
            <div className="bg-amber-500 text-slate-950 p-6 border-2 border-amber-500">
              <div className="font-mono text-xs tracking-[0.2em] uppercase opacity-70">Presencia global</div>
              <div className="font-display font-black text-6xl tracking-tight mt-2">+120</div>
              <div className="font-display font-bold uppercase tracking-tight">Países atendidos</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border-2 border-white/20 p-6">
              <div className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">Categorías</div>
              <div className="font-display font-black text-6xl tracking-tight mt-2 text-white">{CATEGORIES.length}</div>
              <div className="font-display font-bold uppercase tracking-tight text-slate-300">Familias de equipos</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 bg-amber-500 text-slate-950">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-6 text-xs md:text-sm font-display font-bold uppercase tracking-wide md:tracking-wider">
            <span className="flex min-w-0 items-start md:items-center gap-3 py-3 md:py-0 border-b border-slate-950/20 md:border-0 leading-snug">
              <Globe className="w-4 h-4 mt-0.5 md:mt-0 shrink-0" />
              <span className="min-w-0 break-words">Latinoamérica · España · Portugal</span>
            </span>
            <span className="flex min-w-0 items-start md:items-center md:justify-center gap-3 py-3 md:py-0 border-b border-slate-950/20 md:border-0 leading-snug">
              <Shield className="w-4 h-4 mt-0.5 md:mt-0 shrink-0" />
              <span className="min-w-0 break-words">Equipos de fabricación industrial china</span>
            </span>
            <span className="flex min-w-0 items-start md:items-center md:justify-end gap-3 py-3 md:py-0 leading-snug">
              <Wrench className="w-4 h-4 mt-0.5 md:mt-0 shrink-0" />
              <span className="min-w-0 break-words">Servicio posventa y repuestos</span>
            </span>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 items-end">
          <div className="md:col-span-8">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600 mb-3">
              [01] Catálogo de equipos
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
              Líneas de proceso completo<br />para minería y áridos
            </h2>
          </div>
          <div className="md:col-span-4 text-slate-600 text-sm leading-relaxed">
            Investigación, fabricación, comercialización y servicio posventa integrados.
            Desde trituración primaria hasta arena lavada lista para hormigón.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-slate-200 animate-stagger">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <Link
                key={cat.slug}
                to={`/catalogo/${cat.slug}`}
                data-testid={`category-card-${cat.slug}`}
                className="group relative bg-white p-8 border border-slate-200 hover:bg-slate-950 hover:text-white transition-colors duration-300 -m-px flex flex-col gap-4 min-h-[200px]"
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-9 h-9 text-amber-500" strokeWidth={1.5} />
                  <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div>
                  <div className="font-display font-bold uppercase tracking-tight text-lg leading-tight">{cat.name}</div>
                  <p className="text-sm text-slate-500 group-hover:text-slate-300 mt-2 leading-relaxed">{cat.blurb}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-white border-y-2 border-slate-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600 mb-3">
              [02] Sobre nosotros
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight">
              Soluciones de proceso completo para construcción de minas ecológicas.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Henan Greatwall Machinery Co.,Ltd se centra en soluciones de proceso completo para la
              construcción de minas ecológicas. Es un fabricante profesional de sistemas de trituración,
              cribado y líneas de producción de arena a máquina en China y un proveedor de soluciones completas.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Es una empresa de gestión moderna que integra la investigación y el desarrollo de productos,
              la fabricación, la venta de productos y el servicio posventa. Sus productos se utilizan en
              minería metálica y no metálica, nuevos materiales de construcción, transporte, construcción
              urbana, conservación del agua, proyectos hidroeléctricos, desarrollo energético y reciclaje
              de residuos. Los productos y servicios abarcan más de 120 países y regiones.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border-l-4 border-amber-500 pl-4">
                <div className="font-display font-black text-3xl">+120</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Países</div>
              </div>
              <div className="border-l-4 border-amber-500 pl-4">
                <div className="font-display font-black text-3xl">46</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Productos publicados</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] overflow-hidden border-2 border-slate-950">
              <img src={ABOUT_IMG} alt="Instalaciones de Great Wall Machinery" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-amber-500 text-slate-950 p-5 border-2 border-slate-950 max-w-xs hidden md:block">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em]">Great Wall Machinery</div>
              <p className="font-display font-bold uppercase tracking-tight mt-1 leading-tight">
                Proveedor de soluciones completas para la minería y la producción de áridos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-500 mb-3">
              [03] Cotización directa
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              ¿Necesitas equipamiento? <br />
              <span className="text-amber-500">Cotiza directo con nosotros.</span>
            </h2>
            <p className="mt-6 text-slate-400 text-lg max-w-2xl">
              Completa el formulario y nuestro equipo regional te contactará con una propuesta
              técnica y comercial ajustada a tu operación.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4">
            <Link
              to="/contacto"
              data-testid="cta-cotizar-btn"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-7 py-5 text-base btn-brutal inline-flex items-center justify-between"
            >
              Solicitar cotización <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:+56961018000" className="border-2 border-white px-7 py-5 inline-flex items-center justify-between font-display font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-slate-950 transition-colors">
              <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> +569 6101 8000</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

